import {
  useAppSelector,
  useAppDispatch,
} from "../../../../hooks/useTypedSelector";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { LayoutModal } from "../../../general/modules/commons/layoutModal/layoutModal";
import { InvoiceRequestsTable } from "./InvoiceRequestsTable";
import {
  exportInvoiceData,
  getInvoiceData,
} from "../../infrastructure/redux/InvoiceDataSlice";
import { InvoiceRequestRow } from "./InvoiceRequestsTable";

export type InvoiceFilters = {
  relationId?: string;
  status: string;
  InStorageFrom: string;
  InStorageUntil: string;
};

export type InvoiceRow = {
  relation: {
    id: string;
    debtor_number: string;
    invoice_product: string;
    label: string;
    invoice_cleaning?: boolean;
  };
  summary: { label: string; value: string }[];
  requests: InvoiceRequestRow[];
};

export const InvoiceTable = () => {
  const { t } = useTranslation("common");
  const { loading, data, filters, filtersInitialized } = useAppSelector(
    (state: {
      invoiceData: {
        loading: boolean;
        data: InvoiceRow[];
        filters: InvoiceFilters;
        filtersInitialized: boolean;
      };
    }) => state.invoiceData,
  );
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRow | null>(
    null,
  );
  const [exportLoadingId, setExportLoadingId] = useState<string | null>(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState<string | null>(null);

  const buildQueryArgs = useCallback(() => {
    const args: Partial<InvoiceFilters> = {};
    if (filters?.relationId) args.relationId = filters.relationId;
    if (filters?.status) args.status = filters.status;
    if (filters?.InStorageFrom) args.InStorageFrom = filters.InStorageFrom;
    if (filters?.InStorageUntil) args.InStorageUntil = filters.InStorageUntil;
    return args;
  }, [filters]);

  const buildExportQueryArgs = () => {
    const args: {
      relationId?: string;
      status?: string;
      inOutStorageFrom?: string;
      inOutStorageUntil?: string;
    } = {};
    if (filters?.relationId) args.relationId = filters.relationId;
    if (filters?.status) args.status = filters.status;
    if (filters?.InStorageFrom) args.inOutStorageFrom = filters.InStorageFrom;
    if (filters?.InStorageUntil)
      args.inOutStorageUntil = filters.InStorageUntil;
    return args;
  };

  useEffect(() => {
    if (!filtersInitialized) return;
    void dispatch(getInvoiceData(buildQueryArgs()));
  }, [buildQueryArgs, dispatch]);

  const handleShowDetails = (row: InvoiceRow) => {
    setSelectedInvoice(row);
    setShowModal(true);
  };

  const handleExport = async (row: InvoiceRow, invoice: boolean) => {
    if (invoice) {
      setInvoiceLoadingId(row.relation.id);
    } else {
      setExportLoadingId(row.relation.id);
    }
    try {
      const args = {
        ...buildExportQueryArgs(),
        relationId: row.relation.id,
        invoice,
        status: filters.status || "on_invoice",
      };
      const result = await dispatch(exportInvoiceData(args)).unwrap();
      if (invoice) {
        setInvoiceLoadingId(null);
      } else {
        setExportLoadingId(null);
      }
      if (result && typeof result === "object") {
        const url = (result as { url?: string }).url;
        if (url) {
          window.open(url, "_blank");
        }
      }
      if (invoice) {
        void dispatch(getInvoiceData(buildQueryArgs()));
      }
    } catch (err) {
      setExportLoadingId(null);
      setInvoiceLoadingId(null);
      console.error("Error exporting invoice data:", err);
    }
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table crud-table align-middle">
          <thead>
            <tr>
              <th className="py-2 text-nowrap">{t("Clientnr.")}</th>
              <th className="py-2 text-nowrap">{t("Articlenr.")}</th>
              <th className="py-2 text-nowrap">{t("Dealer")}</th>
              <th className="py-2 text-nowrap">{t("Summary")}</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  <span className="spinner-border spinner-border-sm" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  {t("No results")}
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={row.relation.id || idx}>
                  <td>{row.relation.debtor_number}</td>
                  <td>{row.relation.invoice_product}</td>
                  <td>{row.relation.label}</td>
                  <td>
                    {row.summary.map((s) => (
                      <div key={s.label}>
                        {s.label}: <b>{s.value}</b>
                      </div>
                    ))}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-light btn-sm me-2"
                      onClick={() => handleShowDetails(row)}
                    >
                      {t("Show details")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm me-2"
                      disabled={exportLoadingId === row.relation.id}
                      onClick={() => void handleExport(row, false)}
                    >
                      {exportLoadingId === row.relation.id ? (
                        <span className="spinner-border spinner-border-sm me-1" />
                      ) : null}
                      {t("Download export")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={invoiceLoadingId === row.relation.id}
                      onClick={() => void handleExport(row, true)}
                    >
                      {invoiceLoadingId === row.relation.id ? (
                        <span className="spinner-border spinner-border-sm me-1" />
                      ) : null}
                      {t("Export and invoiced")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {selectedInvoice && (
        <LayoutModal
          title={selectedInvoice?.relation?.label || t("Invoice details")}
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setSelectedInvoice(null);
          }}
        >
          <div className="mb-3">
            {selectedInvoice.summary.map((s) => (
              <div key={s.label}>
                {s.label}: <b>{s.value}</b>
              </div>
            ))}
          </div>
          <InvoiceRequestsTable
            requests={selectedInvoice.requests}
            relation={selectedInvoice.relation}
          />
        </LayoutModal>
      )}
    </>
  );
};
